var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Hearing Aid Simulator
 *
 * @description
 * This is a simulator for a hearing aid.
 * It can be used to simulate the effects of a hearing aid on a user's hearing.
 * It toggles between two sound files, one for when the hearing aid is on and one for when it is off.
 */
var HxHearingAidSimulator = /** @class */ (function () {
    function HxHearingAidSimulator() {
        this.initialized = false;
        this.buffers = [];
        this.debug = false;
        this.sources = [];
        this.gainNodes = [];
        this.context = new AudioContext();
        this.masterGain = this.context.createGain();
        this.masterGain.connect(this.context.destination);
        this.masterGain.gain.value = 0.3;
    }
    /**
     * Sets debug mode. (logs to console)
     *
     * @param value Whether to enable debug mode.
     *
     */
    HxHearingAidSimulator.prototype.setDebug = function (debug) {
        this.debug = debug;
        return this;
    };
    /**
     *
     * @returns {HxHearingAidSimulator}
     *
     * @memberOf HxHearingAidSimulator
     * @static
     */
    HxHearingAidSimulator.getInstance = function () {
        if (!HxHearingAidSimulator.instance) {
            HxHearingAidSimulator.instance = new HxHearingAidSimulator();
        }
        return HxHearingAidSimulator.instance;
    };
    /**
     * Destroy instance.
     *
     */
    HxHearingAidSimulator.prototype.destroy = function () {
        delete HxHearingAidSimulator.instance;
    };
    /**
     * Initialize the hearing aid simulator
     *
     * @param files List of files to load as urls
     * @returns Promise that resolves when all files are loaded.
     * @returns
     */
    HxHearingAidSimulator.prototype.initialize = function (files) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            if (_this.initialized) {
                resolve(_this);
            }
            else {
                _this.fetchFiles(files)
                    .then(function () {
                    _this.initialized = true;
                    resolve(_this);
                })
                    .catch(function (error) {
                    reject(error);
                });
            }
        });
    };
    /**
     * Optionally use a given audio context.
     *
     * @param audioContext The AudioContext to use
     */
    HxHearingAidSimulator.prototype.useAudioContext = function (context) {
        if (typeof context === 'undefined')
            return this;
        this.context = context;
        return this;
    };
    /**
     * Optionally use a given audio context.
     *
     * @param audioContext The AudioContext to use
     */
    HxHearingAidSimulator.prototype.useMasterGain = function (masterGain) {
        if (!masterGain)
            return this;
        this.masterGain = this.context.createGain();
        this.masterGain.connect(masterGain);
        this.masterGain.gain.value = 0.3;
        return this;
    };
    /**
     * Sarts the audio playing
     *
     * @param loop Whether to loop the audio (default true)
     */
    HxHearingAidSimulator.prototype.start = function (loop) {
        var _this = this;
        if (loop === void 0) { loop = true; }
        this.log('start');
        this.buffers.forEach(function (buffer) { return _this.playBuffer(buffer); });
    };
    /**
     * Stops the audio playing.
     */
    HxHearingAidSimulator.prototype.stop = function () {
        this.log('stop');
        this.sources.forEach(function (source) { return source.stop(); });
        this.sources = [];
        this.gainNodes = [];
    };
    /**
     * Sets the master volume
     *
     * @param value The volume to set the master gain to.
     *
     */
    HxHearingAidSimulator.prototype.setVolume = function (volume) {
        this.log('setVolume', volume);
        if (typeof volume != 'number' || isNaN(volume))
            return;
        this.masterGain.gain.value = volume;
        this.log(this.masterGain);
    };
    /**
     * Returns the master volume
     * @returns The master volume
     */
    HxHearingAidSimulator.prototype.getVolume = function () {
        this.log('getVolume');
        return this.masterGain.gain.value;
    };
    /**
     * Sets the hearing aid simuator to simulate hearing aids.
     *
     * @param value Whether to simulate wearing a hearing aid.
     *
     */
    HxHearingAidSimulator.prototype.setHearingAidOn = function (value) {
        if (value) {
            this.setAudioFileGain(0, 0);
            this.setAudioFileGain(1, 1);
        }
        else {
            this.setAudioFileGain(0, 1);
            this.setAudioFileGain(1, 0);
        }
    };
    /**
     * Sets the gain of an audio node.
     *
     * @param index The index of the node to set the gain of.
     * @param gain The gain to set the node to.
     */
    HxHearingAidSimulator.prototype.setAudioFileGain = function (index, gain) {
        this.log('setAudioFileGain', this.gainNodes[index], gain, this.gainNodes);
        if (!this.gainNodes[index])
            return;
        this.gainNodes[index].gain.value = gain;
    };
    /**
     * Fetch and load audio files into memory.
     *
     * @param files List of files to load as urls
     * @returns Array of tuples containing the file name and the buffer.
     * @returns
     */
    HxHearingAidSimulator.prototype.fetchFiles = function (files) {
        var _this = this;
        this.log('fetchFiles', files);
        return Promise.all(files.map(function (file) { return _this.fetchFile(file); }))
            .then(function (buffers) { return (_this.buffers = buffers); })
            .catch(function (error) {
            _this.log(error);
            throw error;
        });
    };
    /**
     * Fetches an audio file.
     *
     * @param audioFile Audio file to fetch.
     * @returns The audio file buffer.
     * @private
     */
    HxHearingAidSimulator.prototype.fetchFile = function (file) {
        var _this = this;
        return fetch(file)
            .then(function (response) { return response.arrayBuffer(); })
            .then(function (arrayBuffer) { return _this.context.decodeAudioData(arrayBuffer); })
            .catch(function (error) {
            _this.log(error);
            throw error;
        });
    };
    /**
     * Creates an audio node.
     *
     * @param fileBufferTuple Tuple containing the file name and the buffer.
     * @returns The audio node.
     * @private
     */
    HxHearingAidSimulator.prototype.playBuffer = function (buffer, loop) {
        if (loop === void 0) { loop = true; }
        var gainNode = this.context.createGain();
        gainNode.connect(this.masterGain);
        var source = this.context.createBufferSource();
        source.buffer = buffer;
        source.connect(gainNode);
        source.loop = loop;
        source.start();
        this.sources.push(source);
        this.gainNodes.push(gainNode);
    };
    /**
     * Logs messages to the console if debug is enabled.
     *
     * @param args Optional arguments to log.
     * @private
     */
    HxHearingAidSimulator.prototype.log = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (this.debug) {
            console.log.apply(console, __spreadArray(['HxHearingAidSimulator: '], args, false));
        }
    };
    return HxHearingAidSimulator;
}());
export { HxHearingAidSimulator };
